'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/shared/lib/cn';

/**
 * The "design stage" caption above the render progress bar, replaced as a belt:
 * the outgoing line rolls off the top while the incoming one rolls up from
 * below, both on the same clock. See `stage-belt-*` in `shared/styles/animations.css`
 * for the shared timing (700ms, cubic-bezier(0.33, 0.1, 0.15, 1)).
 *
 * The line is deliberately single-line and clipped: a stage that wrapped would
 * change the strip's height mid-roll and shove the progress bar down with it.
 *
 * Mirrors Apple `DesignStageLine` and Android `DesignStageLine`.
 */

/** Must match the animation duration in `animations.css`. */
const BELT_DURATION_MS = 700;

interface DesignStageLineProps {
  text: string;
  className?: string;
}

export function DesignStageLine({ text, className }: DesignStageLineProps) {
  const [state, setState] = useState<{
    curr: string;
    prev: string | null;
    seq: number;
  }>({ curr: text, prev: null, seq: 0 });

  useEffect(() => {
    setState((s) =>
      s.curr === text ? s : { curr: text, prev: s.curr, seq: s.seq + 1 }
    );
  }, [text]);

  // Drop the outgoing layer once it has left the strip. Without this the DOM
  // would keep every stage the render has ever narrated, stacked and invisible.
  useEffect(() => {
    if (state.prev === null) return undefined;
    const id = setTimeout(
      () => setState((s) => ({ ...s, prev: null })),
      BELT_DURATION_MS
    );
    return () => clearTimeout(id);
  }, [state.prev, state.seq]);

  const { curr, prev, seq } = state;
  // The very first stage of a mount is already correct on screen (a screen
  // reopened mid-render), so it must not roll in from nowhere.
  const isFirst = seq === 0;

  return (
    <span
      className={cn('relative block h-[1.4em] overflow-hidden', className)}
    >
      {prev !== null && (
        <span
          key={`out-${seq}`}
          className="absolute start-0 top-0 block max-w-full truncate animate-stage-belt-out"
        >
          {prev}
        </span>
      )}
      <span
        key={`in-${seq}`}
        className={cn(
          'absolute start-0 top-0 block max-w-full truncate',
          !isFirst && 'animate-stage-belt-in'
        )}
      >
        {curr}
      </span>
    </span>
  );
}
